/**
 * Unified Staff Management Page
 * 
 * Combines Staff Data viewing and Staff Matching into a single unified UI
 * with tabs for different views.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge, LoadingSpinner, EmptyState, Select, useFeatureFlag } from '@tracekit/core';
import { useWorkspace, listEntities, getAPIClient, queryEntities, getAPIBaseUrl, getAuthHeaders } from '@tracekit/core';
import {
  Search,
  User,
  Award,
  Briefcase,
  MapPin,
  Calendar,
  GraduationCap,
  Building2,
  ChevronDown,
  ChevronRight,
  Download,
  Users,
  Settings,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  RefreshCw,
  TrendingUp,
  Filter,
  Clock,
  Shield,
  AlertTriangle,
} from 'lucide-react';
import { StaffMatchingRule, StaffMatchResult } from '../types/rfp';
import { useToastStore } from '@tracekit/core';
import { deduplicateCertificationsStream, getCertificationStats } from '../services/rfpApi';
import { AvailabilityCalendarTab } from '../components/AvailabilityCalendarTab';

interface StaffMember {
  id: string;
  entity_type: string;
  data: {
    name: string;
    role: string;
    experience_years?: number;
    designations: string[];
    qualifications: string[];
    department: string;
    expertise_areas: string[];
    key_capabilities: string;
    location?: {
      city?: string;
      province?: string;
      office_type?: string;
    };
  };
  created_at?: string;
}

interface QualificationEntity {
  id: string;
  entity_type: string;
  data: {
    qualification_type: 'corporate' | 'professional' | 'technical' | 'safety' | 'other';
    certification_name: string;
    issuing_authority: string;
    holder_name?: string;
    holder_type: 'corporate' | 'individual';
    certification_number?: string;
    issue_date?: string;
    expiry_date?: string;
    renewal_required: boolean;
    status: 'active' | 'expiring_soon' | 'expired' | 'pending_renewal';
    applicable_service_lines?: string[];
    scope_description?: string;
  };
  daysUntilExpiry?: number | null;
  computedStatus?: string;
  created_at?: string;
}

type TabType = 'data' | 'matching' | 'qualifications' | 'availability';

export function StaffManagementPage() {
  const enableAvailabilityCalendar = useFeatureFlag('enableAvailabilityCalendar');
  const enableQualificationsCerts = useFeatureFlag('enableQualificationsCerts');
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [activeTab, setActiveTab] = useState<TabType>('data');
  
  // Staff Data state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedDesignation, setSelectedDesignation] = useState<string>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Staff Matching state
  const [matchResults, setMatchResults] = useState<StaffMatchResult[]>([]);
  const [rules, setRules] = useState<StaffMatchingRule[]>([]);
  const [stats, setStats] = useState({
    total_staff: 0,
    matched_staff: 0,
    partial_matches: 0,
    no_matches: 0,
    average_score: 0,
    top_score: 0,
  });
  const [loading, setLoading] = useState(false);
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [editingRule, setEditingRule] = useState<StaffMatchingRule | null>(null);
  const [expandedResult, setExpandedResult] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [testRequirement, setTestRequirement] = useState({
    id: 'test-req-1',
    title: 'Senior Environmental Engineer',
    required_designations: ['P.Eng'],
    min_experience: 5,
    required_expertise: ['Contaminated Sites Assessment & Remediation'],
    location_preference: { province: 'BC' },
    availability_required: true,
  });
  
  // Staff Deduplication state
  const [deduplicationLoading, setDeduplicationLoading] = useState(false);
  const [deduplicationReport, setDeduplicationReport] = useState<any>(null);
  const [showDeduplicationModal, setShowDeduplicationModal] = useState(false);
  const [deduplicationProgress, setDeduplicationProgress] = useState<{
    stage: string;
    message: string;
    percentage: number;
    current?: number;
    total?: number;
    [key: string]: any;
  } | null>(null);

  // Certification Deduplication state
  const [certDeduplicationLoading, setCertDeduplicationLoading] = useState(false);
  const [certDeduplicationReport, setCertDeduplicationReport] = useState<any>(null);
  const [showCertDeduplicationModal, setShowCertDeduplicationModal] = useState(false);
  const [certDeduplicationProgress, setCertDeduplicationProgress] = useState<{
    stage: string;
    message: string;
    percentage: number;
    current?: number;
    total?: number;
    [key: string]: any;
  } | null>(null);

  // Qualifications state
  const [qualSearchQuery, setQualSearchQuery] = useState('');
  const [expandedQuals, setExpandedQuals] = useState<Set<string>>(new Set());
  const [qualPage, setQualPage] = useState(1);
  const [qualPageSize] = useState(20);
  const [qualFilters, setQualFilters] = useState({
    qualificationType: 'all',
    holderType: 'all',
    status: 'all',
  });

  // Fetch staff data
  const { data: staffData, isLoading: staffLoading, error: staffError } = useQuery({
    queryKey: ['staff-members', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const entities = await listEntities(projectId, 'staff_member', 1000);
      return entities.map(e => ({
        id: e.id,
        entity_type: e.entity_type || 'staff_member',
        data: (e.data || {}) as StaffMember['data'],
        created_at: e.created_at || new Date().toISOString(),
      })) as StaffMember[];
    },
    enabled: !!projectId,
  });

  // Fetch matching rules
  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['staff-matching-rules', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const apiClient = getAPIClient();
      try {
        // API client.get returns response.data directly
        const response = await apiClient.get<{ rules: StaffMatchingRule[]; total_rules?: number }>('/api/rfp/staff/rules', {
          params: { project_id: projectId },
        });
        return response.rules || [];
      } catch (error: any) {
        // If 404 or 401, return empty array (endpoint might not exist yet or auth issue)
        if (error?.response?.status === 404 || error?.response?.status === 401) {
          console.warn('[Staff Management] Staff rules endpoint not available, returning empty rules');
          return [];
        }
        throw error;
      }
    },
    enabled: !!projectId,
    retry: false, // Don't retry on 404/401
  });

  useEffect(() => {
    if (rulesData) {
      setRules(rulesData);
      console.log('[Staff Management] Loaded rules:', rulesData.length, 'rules');
    }
  }, [rulesData]);

  // Initialize stats with staff data when it loads
  useEffect(() => {
    if (staffData) {
      setStats(prev => ({
        ...prev,
        total_staff: staffData.length,
      }));
    }
  }, [staffData]);

  // Save rules mutation
  const saveRulesMutation = useMutation({
    mutationFn: async (updatedRules: StaffMatchingRule[]) => {
      if (!projectId) throw new Error('No project ID');
      const apiClient = getAPIClient();
      try {
        // API client.post returns response.data directly
        const response = await apiClient.post('/api/rfp/staff/rules', {
          project_id: projectId,
          rules: updatedRules,
        });
        return response;
      } catch (error: any) {
        console.error('[Staff Management] Error saving rules:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-matching-rules', projectId] });
    },
  });

  // Match staff mutation
  const matchStaffMutation = useMutation({
    mutationFn: async (requirement: any) => {
      if (!projectId) throw new Error('No project ID');
      const apiClient = getAPIClient();
      const response = await apiClient.post('/api/rfp/staff/match', {
        project_id: projectId,
        requirement,
      });
      return response.results || [];
    },
    onSuccess: (results) => {
      setMatchResults(results);
      calculateStats(results);
    },
  });

  const calculateStats = (results: StaffMatchResult[]) => {
    const total = results.length;
    const matched = results.filter(r => (r.match_status || 'no_match') === 'matched').length;
    const partial = results.filter(r => (r.match_status || 'no_match') === 'partial_match').length;
    const noMatch = results.filter(r => (r.match_status || 'no_match') === 'no_match').length;
    const avgScore = total > 0 ? results.reduce((sum, r) => sum + r.match_score, 0) / total : 0;
    const topScore = total > 0 ? Math.max(...results.map(r => r.match_score)) : 0;

    setStats({
      total_staff: staffData?.length || 0, // Use total staff from data, not just matched
      matched_staff: matched,
      partial_matches: partial,
      no_matches: noMatch,
      average_score: avgScore,
      top_score: topScore,
    });
  };

  // Update stats when staff data loads
  useEffect(() => {
    if (staffData && staffData.length > 0 && matchResults.length === 0) {
      // Initialize stats with total staff count
      setStats(prev => ({
        ...prev,
        total_staff: staffData.length,
      }));
    }
  }, [staffData, matchResults.length]);

  // Fetch qualifications with pagination
  const { data: qualificationsData, isLoading: qualsLoading } = useQuery({
    queryKey: ['qualification-entities', projectId, qualPage, qualPageSize],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'qualification_certification',
        limit: qualPageSize,
        offset: (qualPage - 1) * qualPageSize,
      });
      return {
        entities: result.entities.map(e => ({
          id: e.id,
          entity_type: e.entity_type || 'qualification_certification',
          data: (e.data || {}) as QualificationEntity['data'],
          created_at: e.created_at || new Date().toISOString(),
        })) as QualificationEntity[],
        total: result.total_count,
        hasMore: result.pagination.has_more,
      };
    },
    enabled: !!projectId && activeTab === 'qualifications',
  });

  const qualifications = qualificationsData?.entities || [];
  const qualTotalCount = qualificationsData?.total || 0;
  const qualHasMore = qualificationsData?.hasMore || false;

  // Enrich qualifications with computed status
  const enrichedQualifications = useMemo(() => {
    if (!qualifications) return [];
    const now = new Date();
    return qualifications.map(qual => {
      let daysUntilExpiry: number | null = null;
      let computedStatus: string;
      
      if (qual.data.expiry_date) {
        // Parse expiry date - handle both string and timestamp formats
        let expiryDate: Date | null = null;
        
        try {
          if (typeof qual.data.expiry_date === 'number') {
            // Timestamp (seconds or milliseconds)
            expiryDate = new Date(qual.data.expiry_date * (qual.data.expiry_date < 1e10 ? 1000 : 1));
          } else if (typeof qual.data.expiry_date === 'string') {
            // Try ISO format first
            expiryDate = new Date(qual.data.expiry_date);
          }
          
          // Validate the date
          if (expiryDate && !isNaN(expiryDate.getTime())) {
            daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            if (daysUntilExpiry < 0) {
              computedStatus = 'expired';
            } else if (daysUntilExpiry <= 90) {
              computedStatus = 'expiring_soon';
            } else {
              computedStatus = 'active';
            }
          } else {
            // Invalid date - treat as active (no expiry)
            computedStatus = 'active';
          }
        } catch (e) {
          // Parse error - treat as active (no expiry)
          computedStatus = 'active';
        }
      } else {
        // No expiry date - default to active
        computedStatus = 'active';
      }
      
      return { ...qual, daysUntilExpiry, computedStatus };
    });
  }, [qualifications]);

  // Filter qualifications
  const filteredQualifications = useMemo(() => {
    if (!enrichedQualifications) return [];
    return enrichedQualifications.filter(qual => {
      if (qualSearchQuery) {
        const searchLower = qualSearchQuery.toLowerCase();
        const matches = 
          qual.data.certification_name?.toLowerCase().includes(searchLower) ||
          qual.data.issuing_authority?.toLowerCase().includes(searchLower) ||
          qual.data.holder_name?.toLowerCase().includes(searchLower);
        if (!matches) return false;
      }
      if (qualFilters.qualificationType !== 'all' && qual.data.qualification_type !== qualFilters.qualificationType) return false;
      if (qualFilters.holderType !== 'all' && qual.data.holder_type !== qualFilters.holderType) return false;
      if (qualFilters.status !== 'all' && qual.computedStatus !== qualFilters.status) return false;
      return true;
    });
  }, [enrichedQualifications, qualSearchQuery, qualFilters]);

  // Fetch certification statistics (across all entities, not just current page)
  const { data: qualStatsData } = useQuery({
    queryKey: ['certification-stats', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      return await getCertificationStats(projectId);
    },
    enabled: !!projectId && activeTab === 'qualifications',
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: false,
  });

  const qualStats = useMemo(() => {
    // Use API stats if available, otherwise fallback to page-level stats
    if (qualStatsData) {
      return {
        total: qualStatsData.total || 0,
        active: qualStatsData.active || 0,
        expiringSoon: qualStatsData.expiring_soon || 0,
        expired: qualStatsData.expired || 0,
      };
    }
    // Fallback to page-level stats while loading
    if (!enrichedQualifications) return { total: 0, active: 0, expiringSoon: 0, expired: 0 };
    return {
      total: enrichedQualifications.length,
      active: enrichedQualifications.filter(q => q.computedStatus === 'active').length,
      expiringSoon: enrichedQualifications.filter(q => q.computedStatus === 'expiring_soon').length,
      expired: enrichedQualifications.filter(q => q.computedStatus === 'expired').length,
    };
  }, [qualStatsData, enrichedQualifications]);

  const handleTestMatching = async () => {
    setLoading(true);
    try {
      // Validate requirement has at least one criteria
      const hasCriteria = 
        (testRequirement.required_designations && testRequirement.required_designations.length > 0) ||
        (testRequirement.min_experience !== undefined && testRequirement.min_experience !== null) ||
        (testRequirement.required_expertise && testRequirement.required_expertise.length > 0) ||
        (testRequirement.location_preference?.province) ||
        testRequirement.availability_required;
      
      if (!hasCriteria) {
        alert('Please specify at least one matching criteria (designations, experience, expertise, location, or availability)');
        setLoading(false);
        return;
      }
      
      await matchStaffMutation.mutateAsync(testRequirement);
    } catch (error: any) {
      console.error('Error matching staff:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      alert(`Error matching staff: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeduplication = async (dryRun: boolean = true) => {
    setDeduplicationLoading(true);
    setDeduplicationProgress({
      stage: 'starting',
      message: 'Initializing deduplication...',
      percentage: 0,
    });
    setShowDeduplicationModal(true);
    
    try {
      const baseUrl = getAPIBaseUrl();
      const authHeaders = getAuthHeaders();
      
      // For POST with EventSource, we need to use fetch with POST and stream
      const response = await fetch(`${baseUrl}/api/rfp/staff/deduplicate/canonical/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...authHeaders,
        },
        body: JSON.stringify({
          project_id: projectId,
          dry_run: dryRun,
          similarity_threshold: 0.85,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEventType = '';
      
      if (!reader) {
        throw new Error('Response body is not readable');
      }
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.substring(7).trim();
            continue;
          }
          
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              if (currentEventType === 'progress') {
                setDeduplicationProgress({
                  stage: data.stage || 'processing',
                  message: data.message || 'Processing...',
                  percentage: data.percentage || 0,
                  current: data.current,
                  total: data.total,
                  ...data,
                });
              } else if (currentEventType === 'complete') {
                setDeduplicationReport(data);
                setDeduplicationProgress({
                  stage: 'complete',
                  message: 'Deduplication completed successfully!',
                  percentage: 100,
                });
                
                if (!dryRun) {
                  queryClient.invalidateQueries({ queryKey: ['staff-members', projectId] });
                  addToast({
                    type: 'success',
                    title: 'Deduplication Complete',
                    message: `Successfully removed ${data.entities_to_delete || 0} duplicate staff entities.`,
                  });
                } else {
                  addToast({
                    type: 'info',
                    title: 'Deduplication Preview',
                    message: `Found ${data.entities_to_delete || 0} duplicate entities to remove.`,
                  });
                }
              } else if (currentEventType === 'error') {
                throw new Error(data.message || 'Unknown error occurred');
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e, line);
            }
            currentEventType = ''; // Reset after processing
          }
        }
      }
    } catch (error: any) {
      console.error('Error during deduplication:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      
      setDeduplicationProgress({
        stage: 'error',
        message: `Error: ${errorMessage}`,
        percentage: 0,
      });
      
      addToast({
        type: 'error',
        title: 'Deduplication Failed',
        message: errorMessage,
      });
    } finally {
      setDeduplicationLoading(false);
    }
  };

  const handleCertificationDeduplication = async (dryRun: boolean = true) => {
    if (!projectId) return;
    
    setCertDeduplicationLoading(true);
    setCertDeduplicationProgress({
      stage: 'starting',
      message: 'Initializing certification deduplication...',
      percentage: 0,
    });
    setShowCertDeduplicationModal(true);
    
    try {
      const generator = deduplicateCertificationsStream(projectId, dryRun);
      
      for await (const event of generator) {
        if (event.type === 'progress') {
          setCertDeduplicationProgress({
            stage: event.data.stage || 'processing',
            message: event.data.message || 'Processing...',
            percentage: event.data.percentage || 0,
            current: event.data.current,
            total: event.data.total,
            ...event.data,
          });
        } else if (event.type === 'complete') {
          setCertDeduplicationReport(event.data);
          setCertDeduplicationProgress({
            stage: 'complete',
            message: 'Deduplication completed successfully!',
            percentage: 100,
          });
          
          if (!dryRun) {
            // Invalidate both qualifications list and stats
            queryClient.invalidateQueries({ queryKey: ['qualification-entities', projectId] });
            queryClient.invalidateQueries({ queryKey: ['certification-stats', projectId] });
            addToast({
              type: 'success',
              title: 'Certification Deduplication Complete',
              message: `Successfully removed ${event.data.entities_to_delete || 0} duplicate certification entities.`,
            });
          } else {
            addToast({
              type: 'info',
              title: 'Certification Deduplication Preview',
              message: `Found ${event.data.entities_to_delete || 0} duplicate entities to remove.`,
            });
          }
        } else if (event.type === 'error') {
          throw new Error(event.data.message || 'Unknown error occurred');
        }
      }
    } catch (error: any) {
      console.error('Error during certification deduplication:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      
      setCertDeduplicationProgress({
        stage: 'error',
        message: `Error: ${errorMessage}`,
        percentage: 0,
      });
      
      addToast({
        type: 'error',
        title: 'Certification Deduplication Failed',
        message: errorMessage,
      });
    } finally {
      setCertDeduplicationLoading(false);
    }
  };

  // Filter and search staff
  const filteredStaff = React.useMemo(() => {
    if (!staffData) return [];
    
    return staffData.filter((staff) => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const designations = Array.isArray(staff.data.designations) ? staff.data.designations : [];
        const expertiseAreas = Array.isArray(staff.data.expertise_areas) ? staff.data.expertise_areas : [];
        
        const matchesSearch =
          staff.data.name?.toLowerCase().includes(searchLower) ||
          staff.data.role?.toLowerCase().includes(searchLower) ||
          staff.data.department?.toLowerCase().includes(searchLower) ||
          designations.some(d => typeof d === 'string' && d.toLowerCase().includes(searchLower)) ||
          expertiseAreas.some(e => typeof e === 'string' && e.toLowerCase().includes(searchLower)) ||
          (typeof staff.data.key_capabilities === 'string' && staff.data.key_capabilities.toLowerCase().includes(searchLower));
        
        if (!matchesSearch) return false;
      }
      
      if (selectedDepartment !== 'all' && staff.data.department !== selectedDepartment) {
        return false;
      }
      
      if (selectedDesignation !== 'all') {
        const designations = Array.isArray(staff.data.designations) ? staff.data.designations : [];
        if (!designations.includes(selectedDesignation)) {
          return false;
        }
      }
      
      return true;
    });
  }, [staffData, searchTerm, selectedDepartment, selectedDesignation]);

  // Get unique departments and designations
  const departments = React.useMemo(() => {
    if (!staffData) return [];
    const depts = new Set(staffData.map(s => s.data.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [staffData]);

  const designations = React.useMemo(() => {
    if (!staffData) return [];
    const desigs = new Set(staffData.flatMap(s => Array.isArray(s.data.designations) ? s.data.designations : []));
    return Array.from(desigs).sort();
  }, [staffData]);

  // All possible designations from staff data (enum values)
  const allDesignations = [
    'P.Eng', 'P.Geo', 'P.Geo. (Ltd)', 'P.L.Geo', 'R.P.Bio', 'RPBio', 'P.Ag', 'P.Chem', 'PChem',
    'CSAP', 'CSAP (Risk)', 'QPESA', 'EIT', 'GIT', 'EP', 'EPt', 'AScT', 'PMP', 'P.E.', 'P.Biol.',
    'PBD', 'RBTech', 'BC-CESCL', 'NCSO', 'CGA', 'CPA', 'B.Sc.', 'B.Sc.H.', 'B.A.', 'B.A.Sc.',
    'B.ES.', 'B.Eng.', 'B.I.T.', 'BIT', 'B.Tech', 'B.Tech.', 'BBA', 'M.Sc.', 'M.Eng.', 'M.A.Sc.',
    'M.Env.Sc.', 'PhD', 'Dipl. T (Env)', 'Adv.Dip. GIS', 'ADGIS', 'A.Ag'
  ];

  // All possible expertise areas from staff data
  const allExpertiseAreas = [
    'Contaminated Sites Investigation & Remediation',
    'Environmental Assessment',
    'Remediation',
    'Risk Assessment'
  ];

  // Canadian provinces
  const provinces = ['BC', 'AB', 'ON', 'QC', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'YT', 'NT', 'NU'];

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const exportToCSV = () => {
    if (!filteredStaff) return;
    
    const headers = ['Name', 'Role', 'Department', 'Experience (Years)', 'Designations', 'Qualifications', 'Expertise Areas', 'Key Capabilities'];
    const rows = filteredStaff.map(staff => [
      staff.data.name,
      staff.data.role,
      staff.data.department,
      staff.data.experience_years?.toString() || 'N/A',
      (Array.isArray(staff.data.designations) ? staff.data.designations : []).join('; '),
      (Array.isArray(staff.data.qualifications) ? staff.data.qualifications : []).join('; '),
      (Array.isArray(staff.data.expertise_areas) ? staff.data.expertise_areas : []).join('; '),
      typeof staff.data.key_capabilities === 'string' ? staff.data.key_capabilities : (staff.data.key_capabilities ? JSON.stringify(staff.data.key_capabilities) : 'N/A'),
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff-data-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredResults = matchResults.filter(result => {
    const matchStatus = result.match_status || 'no_match';
    const staffName = result.staff_member_name || result.staff_data?.name || '';
    if (filterStatus !== 'all' && matchStatus !== filterStatus) return false;
    if (searchQuery && !staffName.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'matched':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'partial_match':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'no_match':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'partial_match':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'no_match':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (!projectId) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">Please select a project to view staff data.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="w-8 h-8" />
            Staff Management
          </h1>
          <p className="text-gray-600 mt-1">
            View staff data and match staff against RFP requirements
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('data')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'data'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Staff Data ({staffData?.length || 0})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('matching')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'matching'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Staff Matching
            </div>
          </button>
          {enableQualificationsCerts && (
            <button
              onClick={() => setActiveTab('qualifications')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'qualifications'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                Qualifications & Certifications
              </div>
            </button>
          )}
          {enableAvailabilityCalendar && (
            <button
              onClick={() => setActiveTab('availability')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'availability'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Availability Calendar
              </div>
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'data' && (
        <div className="space-y-6">
          {/* Header Actions */}
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              {filteredStaff.length} of {staffData?.length || 0} staff members
            </p>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleDeduplication(true)}
                disabled={deduplicationLoading}
                variant="outline"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${deduplicationLoading ? 'animate-spin' : ''}`} />
                {deduplicationLoading ? 'Analyzing...' : 'Deduplicate Staff'}
              </Button>
              <Button onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search staff..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Department</label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Designation</label>
                <select
                  value={selectedDesignation}
                  onChange={(e) => setSelectedDesignation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Designations</option>
                  {designations.map(desig => (
                    <option key={desig} value={desig}>{desig}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDepartment('all');
                    setSelectedDesignation('all');
                  }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </Card>

          {/* Staff List */}
          {staffLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : staffError ? (
            <Card>
              <div className="text-center py-8">
                <p className="text-red-600 mb-4">Error loading staff data: {staffError instanceof Error ? staffError.message : 'Unknown error'}</p>
                <Button onClick={() => window.location.reload()}>Retry</Button>
              </div>
            </Card>
          ) : !staffData || staffData.length === 0 ? (
            <Card>
              <EmptyState
                icon={<User className="w-12 h-12" />}
                title="No Staff Data"
                description="No staff members found in the database. Import staff data using the import script."
              />
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredStaff.map((staff) => {
                const isExpanded = expandedRows.has(staff.id);
                
                return (
                  <Card key={staff.id} className="hover:shadow-lg transition-shadow">
                    <div
                      className="cursor-pointer"
                      onClick={() => toggleRow(staff.id)}
                    >
                      <div className="flex items-center justify-between p-4">
                        <div className="flex items-center space-x-4 flex-1">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          )}
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold">{staff.data.name}</h3>
                              {Array.isArray(staff.data.designations) && staff.data.designations.length > 0 && (
                                <div className="flex space-x-1">
                                  {staff.data.designations.map((desig, idx) => (
                                    <Badge key={`${desig}-${idx}`} variant="secondary">{desig}</Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">{staff.data.role}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              <span className="flex items-center">
                                <Building2 className="w-4 h-4 mr-1" />
                                {staff.data.department}
                              </span>
                              {staff.data.experience_years && (
                                <span className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  {staff.data.experience_years} years
                                </span>
                              )}
                              {staff.data.location?.province && (
                                <span className="flex items-center">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  {staff.data.location.province}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t p-4 space-y-4">
                        {Array.isArray(staff.data.qualifications) && staff.data.qualifications.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <GraduationCap className="w-4 h-4 mr-2" />
                              Qualifications
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {staff.data.qualifications.map((qual, idx) => (
                                <Badge key={`${qual}-${idx}`} variant="outline">{qual}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {Array.isArray(staff.data.expertise_areas) && staff.data.expertise_areas.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <Briefcase className="w-4 h-4 mr-2" />
                              Expertise Areas
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {staff.data.expertise_areas.map((area, idx) => (
                                <Badge key={`${area}-${idx}`} variant="secondary">{area}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {staff.data.key_capabilities && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <Award className="w-4 h-4 mr-2" />
                              Key Capabilities
                            </h4>
                            <p className="text-gray-700 dark:text-gray-300">
                              {typeof staff.data.key_capabilities === 'string' 
                                ? staff.data.key_capabilities 
                                : JSON.stringify(staff.data.key_capabilities)}
                            </p>
                          </div>
                        )}
                        
                        {staff.data.location && (
                          <div>
                            <h4 className="font-medium mb-2 flex items-center">
                              <MapPin className="w-4 h-4 mr-2" />
                              Location
                            </h4>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {staff.data.location.city && <span>{staff.data.location.city}, </span>}
                              {staff.data.location.province && <span>{staff.data.location.province}</span>}
                              {staff.data.location.office_type && (
                                <span className="ml-2 text-gray-500">({staff.data.location.office_type})</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div>
                          <h4 className="font-medium mb-2 flex items-center">
                            <Clock className="w-4 h-4 mr-2" />
                            Availability
                          </h4>
                          <div className="flex items-center gap-2">
                            <select
                              value={staff.data.availability || 'unknown'}
                              onChange={async (e) => {
                                const newAvailability = e.target.value;
                                try {
                                  const apiClient = getAPIClient();
                                  const response = await apiClient.request({
                                    url: `/api/rfp/staff/availability/${staff.id}`,
                                    method: 'PATCH',
                                    data: {
                                      availability: newAvailability,
                                      project_id: import.meta.env.VITE_PROJECT_ID || '',
                                    },
                                  });
                                  
                                  // Update local state
                                  queryClient.setQueryData(['staff', import.meta.env.VITE_PROJECT_ID], (oldData: any) => {
                                    if (!oldData) return oldData;
                                    return oldData.map((s: StaffMember) => 
                                      s.id === staff.id ? { ...s, data: { ...s.data, availability: newAvailability } } : s
                                    );
                                  });
                                  
                                  // Show success message
                                  alert(`Availability updated to ${newAvailability}`);
                                } catch (error) {
                                  console.error('Error updating availability:', error);
                                  alert(`Failed to update availability: ${error instanceof Error ? error.message : 'Unknown error'}`);
                                }
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                            >
                              <option value="unknown">Unknown</option>
                              <option value="full_time">Full Time</option>
                              <option value="part_time">Part Time</option>
                              <option value="contract">Contract</option>
                            </select>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {staff.data.availability === 'unknown' ? '(treated as available)' : ''}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-400 pt-2 border-t">
                          ID: {staff.id} | Created: {staff.created_at ? new Date(staff.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
              
              {filteredStaff.length === 0 && (
                <Card>
                  <EmptyState
                    icon={<Search className="w-12 h-12" />}
                    title="No Staff Found"
                    description="Try adjusting your search or filter criteria."
                  />
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'matching' && (
        <div className="space-y-6">
          {/* Requirement Input Card */}
          <Card>
            <h3 className="text-lg font-semibold mb-4">RFP Requirement</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <Input
                  value={testRequirement.title}
                  onChange={(e) => setTestRequirement({ ...testRequirement, title: e.target.value })}
                  placeholder="e.g., Senior Environmental Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Minimum Experience (Years)</label>
                <select
                  value={testRequirement.min_experience || ''}
                  onChange={(e) => setTestRequirement({ ...testRequirement, min_experience: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">No minimum</option>
                  {[0, 1, 2, 3, 4, 5, 7, 10, 15, 20, 25, 30].map(years => (
                    <option key={years} value={years}>{years}+ years</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Required Designations
                  {testRequirement.required_designations?.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
                      ({testRequirement.required_designations.length} selected)
                    </span>
                  )}
                </label>
                <select
                  multiple
                  value={testRequirement.required_designations || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value).filter(v => v);
                    setTestRequirement({ ...testRequirement, required_designations: selected });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  size={6}
                >
                  {allDesignations.map(desig => (
                    <option key={desig} value={desig}>{desig}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Hold <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Ctrl</kbd> or <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Cmd</kbd> to select multiple
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Required Expertise
                  {testRequirement.required_expertise?.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
                      ({testRequirement.required_expertise.length} selected)
                    </span>
                  )}
                </label>
                <select
                  multiple
                  value={testRequirement.required_expertise || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value).filter(v => v);
                    setTestRequirement({ ...testRequirement, required_expertise: selected });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  size={6}
                >
                  {allExpertiseAreas.map(exp => (
                    <option key={exp} value={exp}>{exp}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Hold <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Ctrl</kbd> or <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Cmd</kbd> to select multiple
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location Preference (Province)</label>
                <select
                  value={testRequirement.location_preference?.province || ''}
                  onChange={(e) => setTestRequirement({ 
                    ...testRequirement, 
                    location_preference: { province: e.target.value || undefined }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">No preference</option>
                  {provinces.map(prov => (
                    <option key={prov} value={prov}>{prov}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={testRequirement.availability_required || false}
                    onChange={(e) => setTestRequirement({ ...testRequirement, availability_required: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Availability Required</span>
                </label>
              </div>
            </div>
          </Card>

          {/* Header Actions */}
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              Match staff members against RFP requirements
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRulesPanel(!showRulesPanel)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {showRulesPanel ? 'Hide' : 'Show'} Rules
              </button>
              <button
                onClick={handleTestMatching}
                disabled={loading || !staffData || staffData.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Matching...' : 'Test Matching'}
              </button>
            </div>
          </div>

          {/* Rules Panel */}
          {showRulesPanel && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Staff Matching Rules</h3>
                <button
                  onClick={() => setShowRulesPanel(false)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              {rulesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No rules configured. Rules will be loaded from the API.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {rules.length} rule{rules.length !== 1 ? 's' : ''} configured
                  </div>
                  {rules.map((rule) => (
                    <div key={rule.id} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{rule.name}</h4>
                          <Badge variant={rule.priority === 'required' ? 'destructive' : 'secondary'}>
                            {rule.priority}
                          </Badge>
                          {rule.enabled ? (
                            <Badge variant="outline" className="text-green-600">Enabled</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">Disabled</Badge>
                          )}
                        </div>
                        <div className="text-sm font-medium">Weight: {rule.weight}%</div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{rule.description}</p>
                      <div className="text-xs text-gray-500">
                        Category: {rule.category} | Type: {rule.condition?.type || 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Staff</div>
              <div className="text-2xl font-bold">{staffData?.length || stats.total_staff || 0}</div>
            </Card>
            <Card>
              <div className="text-sm text-gray-600 dark:text-gray-400">Matched</div>
              <div className="text-2xl font-bold text-green-600">{stats.matched_staff}</div>
            </Card>
            <Card>
              <div className="text-sm text-gray-600 dark:text-gray-400">Partial</div>
              <div className="text-2xl font-bold text-yellow-600">{stats.partial_matches}</div>
            </Card>
            <Card>
              <div className="text-sm text-gray-600 dark:text-gray-400">No Match</div>
              <div className="text-2xl font-bold text-red-600">{stats.no_matches}</div>
            </Card>
            <Card>
              <div className="text-sm text-gray-600 dark:text-gray-400">Avg Score</div>
              <div className="text-2xl font-bold">{stats.average_score.toFixed(1)}</div>
            </Card>
            <Card>
              <div className="text-sm text-gray-600 dark:text-gray-400">Top Score</div>
              <div className="text-2xl font-bold">{stats.top_score.toFixed(1)}</div>
            </Card>
          </div>

          {/* Matching Results */}
          {filteredResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search results..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Status</option>
                  <option value="matched">Matched</option>
                  <option value="partial_match">Partial Match</option>
                  <option value="no_match">No Match</option>
                </select>
              </div>

              {filteredResults.map((result) => (
                <Card key={result.staff_member_id} className="hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-4 flex-1">
                      {getStatusIcon(result.match_status)}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-lg font-semibold">{result.staff_member_name || result.staff_data?.name || 'Unknown'}</h3>
                          <Badge className={getStatusColor(result.match_status || 'no_match')}>
                            {(result.match_status || 'no_match').replace('_', ' ')}
                          </Badge>
                          <span className="text-lg font-bold text-blue-600">
                            {result.match_score.toFixed(1)}%
                          </span>
                        </div>
                        {result.explanation ? (
                          <div className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
                            <div className="line-clamp-2">
                              {result.explanation.split('\n').slice(0, 2).join('\n')}
                              {result.explanation.split('\n').length > 2 && (
                                <span className="text-blue-600 dark:text-blue-400"> ... (click to expand)</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-gray-500 dark:text-gray-400 mt-1 italic text-sm">
                            Click to expand for match details
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedResult(expandedResult === result.staff_member_id ? null : result.staff_member_id)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                    >
                      {expandedResult === result.staff_member_id ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  
                  {expandedResult === result.staff_member_id && (
                    <div className="border-t p-4 space-y-4">
                      <div>
                        <h4 className="font-medium mb-2">Matched Rules:</h4>
                        {result.matched_rules && result.matched_rules.length > 0 ? (
                          <div className="space-y-2">
                            {result.matched_rules.map((rule: any, idx: number) => (
                              <div key={idx} className="text-sm p-2 bg-gray-50 dark:bg-gray-800 rounded">
                                <div className="font-medium">{rule.rule_name || `Rule ${idx + 1}`}</div>
                                <div className="text-gray-600 dark:text-gray-400">{rule.message || rule.explanation}</div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Score: {typeof rule.score_contribution === 'number' ? rule.score_contribution.toFixed(1) : 'N/A'}% | Weight: {typeof rule.weight === 'number' ? rule.weight : 'N/A'}%
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No rules matched</p>
                        )}
                      </div>
                      {result.explanation && (
                        <div>
                          <h4 className="font-medium mb-2">Detailed Explanation:</h4>
                          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700 font-mono text-xs leading-relaxed">
                            {result.explanation.split('\n').map((line, idx) => {
                              // Style different parts of the explanation
                              if (line.includes('Match Score:')) {
                                return <div key={idx} className="font-bold text-blue-600 dark:text-blue-400 mb-2">{line}</div>;
                              } else if (line.includes('✅') || line.includes('All Required Criteria Met')) {
                                return <div key={idx} className="text-green-600 dark:text-green-400">{line}</div>;
                              } else if (line.includes('❌') || line.includes('Failed')) {
                                return <div key={idx} className="text-red-600 dark:text-red-400">{line}</div>;
                              } else if (line.includes('⚠️')) {
                                return <div key={idx} className="text-yellow-600 dark:text-yellow-400">{line}</div>;
                              } else if (line.includes('📊') || line.includes('💡')) {
                                return <div key={idx} className="font-semibold mt-2 mb-1">{line}</div>;
                              } else if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                                return <div key={idx} className="ml-4">{line}</div>;
                              } else if (line.trim().startsWith('Score:') || line.trim().startsWith('Weight:')) {
                                return <div key={idx} className="ml-6 text-gray-500 dark:text-gray-400">{line}</div>;
                              }
                              return <div key={idx}>{line || '\u00A0'}</div>;
                            })}
                          </div>
                        </div>
                      )}
                      {/* Show full staff data if available */}
                      {result.staff_data && (
                        <div>
                          <h4 className="font-medium mb-2">Staff Details:</h4>
                          <div className="text-sm space-y-2">
                            <div><strong>Role:</strong> {result.staff_data.role || 'N/A'}</div>
                            <div><strong>Department:</strong> {result.staff_data.department || 'N/A'}</div>
                            {result.staff_data.experience_years && (
                              <div><strong>Experience:</strong> {result.staff_data.experience_years} years</div>
                            )}
                            {result.staff_data.designations && result.staff_data.designations.length > 0 && (
                              <div><strong>Designations:</strong> {result.staff_data.designations.join(', ')}</div>
                            )}
                            {result.staff_data.qualifications && result.staff_data.qualifications.length > 0 && (
                              <div><strong>Qualifications:</strong> {result.staff_data.qualifications.join(', ')}</div>
                            )}
                            {result.staff_data.expertise_areas && result.staff_data.expertise_areas.length > 0 && (
                              <div><strong>Expertise:</strong> {result.staff_data.expertise_areas.join(', ')}</div>
                            )}
                            {result.staff_data.key_capabilities && (
                              <div><strong>Capabilities:</strong> {result.staff_data.key_capabilities}</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {filteredResults.length === 0 && matchResults.length === 0 && (
            <Card>
              <EmptyState
                icon={<TrendingUp className="w-12 h-12" />}
                title="No Matching Results"
                description="Click 'Test Matching' to match staff against the test requirement."
              />
            </Card>
          )}
        </div>
      )}

      {/* Qualifications Tab */}
      {activeTab === 'qualifications' && enableQualificationsCerts && (
        <div className="space-y-6 mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-sm text-gray-600">Total Certifications</div>
              <div className="text-2xl font-bold">{qualStats.total}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-600">Active</div>
              <div className="text-2xl font-bold text-green-600">{qualStats.active}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-600">Expiring Soon</div>
              <div className="text-2xl font-bold text-yellow-600">{qualStats.expiringSoon}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-600">Expired</div>
              <div className="text-2xl font-bold text-red-600">{qualStats.expired}</div>
            </Card>
          </div>

          {/* Alerts */}
          {qualStats.expiringSoon > 0 && (
            <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 p-4 text-gray-900 dark:text-gray-100">
              <div className="flex items-center gap-3 text-yellow-800 dark:text-yellow-200">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <p className="font-semibold">Action Required</p>
                  <p className="text-sm">{qualStats.expiringSoon} certification{qualStats.expiringSoon !== 1 ? 's' : ''} expiring within 90 days.</p>
                </div>
              </div>
            </Card>
          )}
          {qualStats.expired > 0 && (
            <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 p-4 text-gray-900 dark:text-gray-100">
              <div className="flex items-center gap-3 text-red-800 dark:text-red-200">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <p className="font-semibold">Critical: Expired Certifications</p>
                  <p className="text-sm">{qualStats.expired} certification{qualStats.expired !== 1 ? 's' : ''} expired.</p>
                </div>
              </div>
            </Card>
          )}

          {/* Header Actions */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-600">
              Manage and deduplicate certification entities
            </p>
            <Button
              onClick={() => handleCertificationDeduplication(true)}
              disabled={certDeduplicationLoading}
              variant="outline"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${certDeduplicationLoading ? 'animate-spin' : ''}`} />
              {certDeduplicationLoading ? 'Analyzing...' : 'Deduplicate Certifications'}
            </Button>
          </div>

          {/* Search & Filters */}
          <Card className="p-4">
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    value={qualSearchQuery}
                    onChange={(e) => setQualSearchQuery(e.target.value)}
                    placeholder="Search certifications..."
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <Select value={qualFilters.qualificationType} onChange={(e) => setQualFilters({ ...qualFilters, qualificationType: e.target.value })}>
                    <option value="all">All Types</option>
                    <option value="corporate">Corporate</option>
                    <option value="professional">Professional</option>
                    <option value="technical">Technical</option>
                    <option value="safety">Safety</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Holder</label>
                  <Select value={qualFilters.holderType} onChange={(e) => setQualFilters({ ...qualFilters, holderType: e.target.value })}>
                    <option value="all">All</option>
                    <option value="corporate">Corporate</option>
                    <option value="individual">Individual</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <Select value={qualFilters.status} onChange={(e) => setQualFilters({ ...qualFilters, status: e.target.value })}>
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="expiring_soon">Expiring Soon</option>
                    <option value="expired">Expired</option>
                  </Select>
                </div>
              </div>
            </div>
          </Card>

          {/* Loading State */}
          {qualsLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {/* Qualifications List */}
          {!qualsLoading && filteredQualifications.length === 0 && (
            <Card>
              <EmptyState
                icon={<Award className="w-12 h-12" />}
                title="No Certifications Found"
                description="No qualifications match your search criteria."
              />
            </Card>
          )}

          {!qualsLoading && filteredQualifications.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  Showing {((qualPage - 1) * qualPageSize) + 1}-{Math.min(qualPage * qualPageSize, qualTotalCount)} of {qualTotalCount} certifications
                </p>
              </div>
              
              <div className="space-y-3">
                {filteredQualifications.map((qual) => {
                const isExpanded = expandedQuals.has(qual.id);
                return (
                  <Card key={qual.id} className="hover:shadow-lg transition-shadow">
                    <div className="cursor-pointer p-4" onClick={() => {
                      const newExpanded = new Set(expandedQuals);
                      if (newExpanded.has(qual.id)) newExpanded.delete(qual.id);
                      else newExpanded.add(qual.id);
                      setExpandedQuals(newExpanded);
                    }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                          <Shield className={`w-5 h-5 ${qual.computedStatus === 'active' ? 'text-green-600' : qual.computedStatus === 'expiring_soon' ? 'text-yellow-600' : 'text-red-600'}`} />
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h3 className="text-lg font-semibold">{qual.data.certification_name}</h3>
                              <Badge variant="default" className={qual.computedStatus === 'active' ? 'bg-green-600' : qual.computedStatus === 'expiring_soon' ? 'bg-yellow-600' : 'bg-red-600'}>
                                {qual.computedStatus === 'active' ? 'Active' : qual.computedStatus === 'expiring_soon' ? 'Expiring Soon' : 'Expired'}
                              </Badge>
                              <Badge variant="outline">{qual.data.qualification_type}</Badge>
                            </div>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                              <span className="flex items-center">
                                <Building2 className="w-4 h-4 mr-1" />
                                {qual.data.issuing_authority}
                              </span>
                              {qual.data.expiry_date && (
                                <span className="flex items-center">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  Expires: {new Date(qual.data.expiry_date).toLocaleDateString()}
                                  {qual.daysUntilExpiry !== null && qual.daysUntilExpiry !== undefined && (
                                    <span className={`ml-1 ${qual.daysUntilExpiry < 0 ? 'text-red-600' : qual.daysUntilExpiry <= 90 ? 'text-yellow-600' : 'text-green-600'}`}>
                                      ({qual.daysUntilExpiry < 0 ? 'Expired' : `${qual.daysUntilExpiry} days`})
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {qual.data.certification_number && (
                            <div><span className="font-medium">Number:</span> {qual.data.certification_number}</div>
                          )}
                          {qual.data.issue_date && (
                            <div><span className="font-medium">Issued:</span> {new Date(qual.data.issue_date).toLocaleDateString()}</div>
                          )}
                          <div><span className="font-medium">Holder:</span> {qual.data.holder_name || 'Corporate'} ({qual.data.holder_type})</div>
                        </div>
                        {qual.data.scope_description && (
                          <div>
                            <h4 className="font-medium mb-1">Scope</h4>
                            <p className="text-sm text-gray-700">{qual.data.scope_description}</p>
                          </div>
                        )}
                        {qual.data.applicable_service_lines && qual.data.applicable_service_lines.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Service Lines</h4>
                            <div className="flex flex-wrap gap-2">
                              {qual.data.applicable_service_lines.map((line, idx) => (
                                <Badge key={idx} variant="secondary">{line}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
              </div>

              {/* Pagination Controls */}
              <Card className="p-4 mt-4">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setQualPage(p => Math.max(1, p - 1))}
                    disabled={qualPage === 1}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, Math.ceil(qualTotalCount / qualPageSize)) }, (_, i) => i + 1).map(pageNum => (
                      <Button
                        key={pageNum}
                        variant={qualPage === pageNum ? 'default' : 'outline'}
                        onClick={() => setQualPage(pageNum)}
                        className="w-10"
                      >
                        {pageNum}
                      </Button>
                    ))}
                    {Math.ceil(qualTotalCount / qualPageSize) > 5 && <span className="text-gray-500">...</span>}
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => setQualPage(p => p + 1)}
                    disabled={!qualHasMore}
                  >
                    Next
                  </Button>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Deduplication Modal */}
      {showDeduplicationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white dark:bg-gray-800 pb-4 border-b">
              <h2 className="text-2xl font-bold">
                {deduplicationLoading ? 'Staff Deduplication Progress' : 'Staff Deduplication Report'}
              </h2>
              <button
                onClick={() => {
                  if (!deduplicationLoading) {
                    setShowDeduplicationModal(false);
                  }
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                disabled={deduplicationLoading}
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Section */}
            {deduplicationLoading && deduplicationProgress && (
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{deduplicationProgress.message}</span>
                    <span className="text-gray-600 dark:text-gray-400">{deduplicationProgress.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${deduplicationProgress.percentage}%` }}
                    />
                  </div>
                  {deduplicationProgress.current !== undefined && deduplicationProgress.total !== undefined && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Processing {deduplicationProgress.current} of {deduplicationProgress.total}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <LoadingSpinner className="w-4 h-4" />
                  <span>Please wait, this may take several minutes...</span>
                </div>
              </div>
            )}

            {/* Report Section */}
            {deduplicationReport && !deduplicationLoading && (
              <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Entities</div>
                  <div className="text-2xl font-bold">{deduplicationReport.total_entities || 0}</div>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Unique Staff</div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{deduplicationReport.unique_staff || 0}</div>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                  <div className="text-sm text-gray-600 dark:text-gray-400">To Delete</div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">{deduplicationReport.entities_to_delete || 0}</div>
                </div>
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Reduction</div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {deduplicationReport.total_entities > 0
                      ? Math.round(((deduplicationReport.entities_to_delete || 0) / deduplicationReport.total_entities) * 100)
                      : 0}%
                  </div>
                </div>
              </div>

              {/* Top Duplicates - Sample of entities to be deleted */}
              {deduplicationReport.top_duplicates && deduplicationReport.top_duplicates.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Sample of Entities to Delete</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    These are examples of duplicate/outdated entities that will be removed:
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {deduplicationReport.top_duplicates.slice(0, 20).map((dup: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold text-gray-400">#{idx + 1}</span>
                          <span className="font-medium">{dup.name || 'NO NAME'}</span>
                        </div>
                        <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          {dup.reason || 'Duplicate'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t sticky bottom-0 bg-white dark:bg-gray-800">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {deduplicationReport.dry_run ? (
                      <span>⚠️ This is a preview. No changes have been made.</span>
                    ) : (
                      <span className="text-green-600 font-semibold">✅ Deduplication completed successfully!</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowDeduplicationModal(false)}
                    >
                      Close
                    </Button>
                    {deduplicationReport.dry_run && (
                      <Button
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete ${deduplicationReport.entities_to_delete} duplicate staff entities? This cannot be undone.`)) {
                            setShowDeduplicationModal(false);
                            handleDeduplication(false);
                          }
                        }}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Execute Deduplication
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Certification Deduplication Modal */}
      {showCertDeduplicationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white dark:bg-gray-800 pb-4 border-b">
              <h2 className="text-2xl font-bold">
                {certDeduplicationLoading ? 'Certification Deduplication Progress' : 'Certification Deduplication Report'}
              </h2>
              <button
                onClick={() => {
                  if (!certDeduplicationLoading) {
                    setShowCertDeduplicationModal(false);
                  }
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                disabled={certDeduplicationLoading}
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Section */}
            {certDeduplicationLoading && certDeduplicationProgress && (
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{certDeduplicationProgress.message}</span>
                    <span className="text-gray-600 dark:text-gray-400">{certDeduplicationProgress.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${certDeduplicationProgress.percentage}%` }}
                    />
                  </div>
                  {certDeduplicationProgress.current !== undefined && certDeduplicationProgress.total !== undefined && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Processing {certDeduplicationProgress.current} of {certDeduplicationProgress.total}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <LoadingSpinner className="w-4 h-4" />
                  <span>Please wait, this may take several minutes...</span>
                </div>
              </div>
            )}

            {/* Report Section */}
            {certDeduplicationReport && !certDeduplicationLoading && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Entities</div>
                    <div className="text-2xl font-bold">{certDeduplicationReport.total_entities || 0}</div>
                  </div>
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Unique Certifications</div>
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{certDeduplicationReport.unique_certifications || 0}</div>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                    <div className="text-sm text-gray-600 dark:text-gray-400">To Delete</div>
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{certDeduplicationReport.entities_to_delete || 0}</div>
                  </div>
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-gray-900 dark:text-gray-100">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Reduction</div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {certDeduplicationReport.total_entities > 0
                        ? Math.round(((certDeduplicationReport.entities_to_delete || 0) / certDeduplicationReport.total_entities) * 100)
                        : 0}%
                    </div>
                  </div>
                </div>

                {/* Top Duplicates */}
                {certDeduplicationReport.top_duplicates && certDeduplicationReport.top_duplicates.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Top Duplicate Groups</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {certDeduplicationReport.top_duplicates.slice(0, 20).map((dup: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold text-gray-400">#{idx + 1}</span>
                            <div>
                              <span className="font-medium">{dup.certification_name || 'Unknown'}</span>
                              {dup.issuing_body && (
                                <span className="text-sm text-gray-500 ml-2">({dup.issuing_body})</span>
                              )}
                            </div>
                          </div>
                          <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            {dup.duplicate_count} duplicates
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t sticky bottom-0 bg-white dark:bg-gray-800">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {certDeduplicationReport.dry_run ? (
                      <span>⚠️ This is a preview. No changes have been made.</span>
                    ) : (
                      <span className="text-green-600 font-semibold">✅ Deduplication completed successfully!</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowCertDeduplicationModal(false)}
                    >
                      Close
                    </Button>
                    {certDeduplicationReport.dry_run && (
                      <Button
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete ${certDeduplicationReport.entities_to_delete} duplicate certification entities? This cannot be undone.`)) {
                            setShowCertDeduplicationModal(false);
                            handleCertificationDeduplication(false);
                          }
                        }}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Execute Deduplication
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Availability Calendar Tab */}
      {activeTab === 'availability' && enableAvailabilityCalendar && (
        <AvailabilityCalendarTab
          projectId={projectId}
          staffMembers={staffData || []}
        />
      )}
    </div>
  );
}

